import { HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';

type MapValuesToKeysIfAllowed<T> = {
  [K in keyof T]: T[K] extends PropertyKey ? K : never;
};
type Filter<T> = MapValuesToKeysIfAllowed<T>[keyof T];

@Injectable({
  providedIn: 'root',
})
export class HelperFunctionsService {
  defaultPageSize = 25;

  definitions = {
    Location: [],
    Product: [],
    Customer: [],
  };

  constructor(private router: Router) {}

  handleDefinitions(ids, target) {
    if (this.definitions[target]) {
      this.definitions[target] = ids;
    } else {
      throw new Error(`Target ${target} not found`);
    }

    const allIds = [
      ...this.definitions.Location.map((location) => location.id),
      ...this.definitions.Product.map((product) => product.id),
      ...this.definitions.Customer.map((customer) => customer.id),
    ];

    let targetId = 0;
    if (this.definitions.Location.length) targetId += 4;
    if (this.definitions.Product.length) targetId += 2;
    if (this.definitions.Customer.length) targetId += 1;

    return { ids: allIds, target: targetId };
  }

  isEqual(obj1: any, obj2: any, key: string): boolean {
    return obj1[key] === obj2[key];
  }

  removeDuplicatesFromObjectArray(arr: any[], key: string): any[] {
    return arr.filter((item, index, self) => {
      return index === self.findIndex((i) => this.isEqual(i, item, key));
    });
  }

  removeDuplicatesAndNullsFromArray(array: any[]) {
    // Check if the input is an array
    if (!Array.isArray(array)) {
      return array; // If not an array, return the value as it is
    }

    // Remove null and undefined values from the array
    const nonNullArray = array.filter(
      (value) => value !== null && value !== undefined
    );

    // Remove duplicate values from the array
    return [...new Set(nonNullArray)];
  }

  calculateSum(values) {
    return values.reduce((total, current) => {
      return total + current;
    }, 0);
  }

  addSpacedAfterCapitals(string) {
    return string.replace(/([A-Z])/g, ' $1').trim();
  }

  generateUrlQuery(params: any) {
    // set default take
    if (!params['pageSize']) params['pageSize'] = this.defaultPageSize;
    //default to page one
    if (!params['page']) params['page'] = 1;
    const queryParams: string[] = [];
    Object.entries(params).forEach(([key, value]) => {
      queryParams.push(`${key}=${value}`);
    });

    if (queryParams.length > 0) {
      return `?${queryParams.join('&')}`;
    }
    return '';
  }

  //Removes null values.

  sanitizeParams(params: any) {
    for (const propName in params) {
      if (params[propName] === null || params[propName] === '') {
        delete params[propName];
      }
    }

    return params;
  }

  trimFormGroupValues(formGroup) {
    // Iterate through the controls of the FormGroup
    Object.keys(formGroup.controls).forEach((controlName) => {
      const control = formGroup.get(controlName);

      // Check if the control value is a string before trimming
      if (typeof control.value === 'string') {
        control.setValue(control.value.trim());
      }
    });
    return formGroup;
  }

  getEnumKeyWithValue(val, targetEnum) {
    const indexOfValue = Object.values(targetEnum).indexOf(val as any);
    return Object.keys(targetEnum)[indexOfValue];
  }

  removeWhiteSpaces(string: string) {
    return string.replace(/\s/g, '');
  }

  addLeadingZeros(n) {
    if (n <= 9) {
      return '0' + n;
    }
    return n;
  }

  friendlyDate(
    date: number | string | Date,
    yearFirst = true,
    includeTime = true,
    includeDate = true,
    diffDays = false,
    daysAgo = 5
  ) {
    if (!date) return null;

    const now = new Date();
    const d = new Date(date);
    let dateString = '';
    let days = this.diffDays(d, now);

    if (diffDays && days <= daysAgo) {
      //Calculate difference in days
      const utc1 = Date.UTC(d.getFullYear(), d.getMonth(), d.getDate());
      const utc2 = Date.UTC(now.getFullYear(), now.getMonth(), now.getDate());

      if (days === 0) {
        dateString = 'Today';
      } else if (days === 1) {
        dateString = 'Yesterday';
      } else {
        if (utc1 > utc2) dateString = 'in ';
        if (days < 0) days = days * -1; // avoid showing negative numbers for future dates.
        dateString += days + ' days';
        if (utc1 < utc2) dateString += ' ago';
      }
    }

    // Basically can replace this function with
    // d.toLocaleString('en-GB'); 06/05/2022, 14:41:04
    if (includeDate || !dateString || dateString === '') {
      if (yearFirst) {
        dateString +=
          this.addLeadingZeros(d.getDate()) +
          '/' +
          this.addLeadingZeros(d.getMonth() + 1) +
          '/' +
          d.getFullYear();
      } else {
        dateString +=
          this.addLeadingZeros(d.getDate()) +
          '/' +
          this.addLeadingZeros(d.getMonth() + 1) +
          '/' +
          d.getFullYear();
      }
    }

    if (includeTime || (diffDays && days > daysAgo)) {
      const hours = ('0' + d.getHours()).slice(-2);
      const minutes = ('0' + d.getMinutes()).slice(-2);
      const seconds = ('0' + d.getSeconds()).slice(-2);

      dateString += `, ${hours}:${minutes}:${seconds}`;
    }
    return dateString;
  }

  diffDays(
    startDate: number | string | Date,
    endDate: number | string | Date = new Date()
  ) {
    const d = new Date(startDate);
    const e = new Date(endDate);
    const _MS_PER_DAY = 1000 * 60 * 60 * 24;
    const utc1 = Date.UTC(d.getFullYear(), d.getMonth(), d.getDate());
    const utc2 = Date.UTC(e.getFullYear(), e.getMonth(), e.getDate());
    return Math.floor((utc2 - utc1) / _MS_PER_DAY);
  }

  areDatesWithinSpecifiedDays(
    selectedDates: {
      fromDate: string;
      toDate: string;
    },
    numberOfDays = 30
  ): boolean {
    const fromDate = new Date(selectedDates.fromDate);
    const toDate = new Date(selectedDates.toDate);
    const daysInMillis = numberOfDays * 24 * 60 * 60 * 1000; // days in milliseconds

    // Calculate the difference in milliseconds between fromDate and toDate
    const timeDifference = toDate.getTime() - fromDate.getTime();

    // Compare the time difference with the threshold (30 days)
    return timeDifference <= daysInMillis;
  }

  updateUrlNoRefresh(url, clearState = false) {
    // I'd suggest using the angular router instead of window
    const currentState = clearState == false ? window.history.state : null; // Get the current state object
    const newUrl = new URL(url, window.location.href);
    window.history.replaceState(currentState, '', newUrl.href);
  }

  //The angular router version of this doesnt really work with the paginator too well...

  // updateUrlNoRefresh(url: string, clearState = false) {
  //   // Get the current state object
  //   const currentState =
  //     clearState === false
  //       ? this.router.getCurrentNavigation()?.extras.state
  //       : null;

  //   // Navigate to the new URL using the Angular Router
  //   this.router.navigateByUrl(url, { state: currentState });
  // }

  getFileName(
    response: HttpResponse<Blob>,
    filename = 'boma_stocktake_report'
  ) {
    const currentDate = new Date();
    const dateString = currentDate
      .toLocaleDateString('en-US', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      })
      .replace(/\//g, '_'); // Replace slashes with underscores

    const timeString = currentDate
      .toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
      })
      .replace(/:/g, '_'); // Replace colons with underscores
    return `${filename}_${dateString}__${timeString}.xlsx`;
  }

  //EXPAND ON THIS SO ITS MORE DYNAMIC KEEPING TRACK OF THE STYLESHEETS AND REMOVING THEM DYNAMICALLY
  printSettings(orientation = 'landscape', padding = true) {
    const landscapeStylesheetLink = document.createElement('link');
    landscapeStylesheetLink.rel = 'stylesheet';
    landscapeStylesheetLink.href =
      orientation === 'landscape' ? 'assets/css/landscape.css' : 'portrait.css';
    document.head.appendChild(landscapeStylesheetLink);

    if (!padding) {
      const minimalistStylesheetLink = document.createElement('link');
      minimalistStylesheetLink.rel = 'stylesheet';
      minimalistStylesheetLink.href = 'assets/css/minimalist.css';
      document.head.appendChild(minimalistStylesheetLink);
    }
  }

  groupBy<T extends Record<PropertyKey, any>, Key extends Filter<T>>(
    arr: T[],
    key: Key
  ): Record<T[Key], T[]> {
    return arr.reduce(
      (accumulator, val) => {
        const groupedKey = val[key];
        if (!accumulator[groupedKey]) {
          accumulator[groupedKey] = [];
        }
        accumulator[groupedKey].push(val);
        return accumulator;
      },
      {} as Record<T[Key], T[]>
    );
  }

  isMobileDevice(): boolean {
    const userAgent = navigator.userAgent.toLowerCase();
    return (
      userAgent.includes('android') ||
      userAgent.includes('iphone') ||
      userAgent.includes('ipad') ||
      userAgent.includes('windows phone')
    );
  }

  viewUserNewTab(userId, entityId) {
    const url = ['/users/details', entityId, userId];
    const urlStr = url.join('/');
    window.open(urlStr, '_blank');
  }
}
